import React, { useState } from "react";
import { X, Sparkles, Image as ImageIcon, Eye, FileText } from "lucide-react";
import { slugify } from "../../constants";

const PRESET_IMAGES = [
  "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1200&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1592919505780-303950717480?w=1200&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1530053969600-caed2596d242?w=1200&auto=format&fit=crop&q=80",
];

const CATEGORIES = ["Guides & Tips", "Course Spotlights", "Family Fun", "Industry News", "Events & News"];

const BlogForm = ({ blog, onSave, onCancel }) => {
  const isNew = !blog || !blog.id;
  const [preview, setPreview] = useState(false);
  const [form, setForm] = useState({
    title: blog?.title || "",
    slug: blog?.slug || "",
    category: blog?.category || "Guides & Tips",
    coverImage: blog?.coverImage || PRESET_IMAGES[0],
    author: blog?.author || "Admin",
    readTime: blog?.readTime || "5 min read",
    excerpt: blog?.excerpt || "",
    content: blog?.content || "",
    published: blog?.published !== undefined ? blog.published : true,
    publishedAt: blog?.publishedAt || new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
  });

  const set = (k, v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === "title" && isNew) {
        next.slug = slugify(v);
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert("Please enter a title");
    if (!form.content.trim()) return alert("Please enter article content");
    onSave({
      ...form,
      slug: form.slug.trim() || slugify(form.title),
    });
  };

  const inputCls =
    "w-full px-3.5 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] focus:border-transparent transition-all";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-4xl w-full my-8 overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[92vh]">
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/70">
          <div>
            <h3 className="text-xl font-black text-gray-900 tracking-tight">
              {isNew ? "Create New Blog Post" : "Edit Blog Post"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Write and publish engaging articles for the Mini Golf Journal</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreview(!preview)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" /> {preview ? "Edit Mode" : "Preview"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {preview ? (
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden h-64 border border-gray-200">
                <img src={form.coverImage} alt={form.title} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="px-3 py-1 bg-[#e5243b] text-white text-xs font-bold rounded-full">
                  {form.category}
                </span>
                <h1 className="text-3xl font-black text-gray-900 mt-3">{form.title || "Untitled Article"}</h1>
                <p className="text-xs text-gray-500 mt-2">
                  By {form.author} · {form.readTime} · {form.publishedAt}
                </p>
              </div>
              {form.excerpt && (
                <div className="p-4 bg-gray-50 border-l-4 border-[#e5243b] text-gray-700 italic text-sm">
                  {form.excerpt}
                </div>
              )}
              <div className="prose max-w-none text-gray-800 text-sm whitespace-pre-line leading-relaxed">
                {form.content || "No content written yet."}
              </div>
            </div>
          ) : (
            <form id="blog-form" onSubmit={handleSubmit} className="space-y-5">
              {/* Title & Slug */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Article Title *
                  </label>
                  <input
                    className={inputCls}
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="e.g. 10 Best Mini Golf Spots in Alabama"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    URL Slug
                  </label>
                  <input
                    className={inputCls}
                    value={form.slug}
                    onChange={(e) => set("slug", e.target.value)}
                    placeholder="best-mini-golf-spots-alabama"
                  />
                </div>
              </div>

              {/* Category, Author & Read Time */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Category
                  </label>
                  <select
                    className={inputCls}
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Author
                  </label>
                  <input
                    className={inputCls}
                    value={form.author}
                    onChange={(e) => set("author", e.target.value)}
                    placeholder="Editorial Team"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Reading Time
                  </label>
                  <input
                    className={inputCls}
                    value={form.readTime}
                    onChange={(e) => set("readTime", e.target.value)}
                    placeholder="5 min read"
                  />
                </div>
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Cover Image URL
                </label>
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    value={form.coverImage}
                    onChange={(e) => set("coverImage", e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                  />
                </div>
                <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1">
                  <span className="text-[11px] font-semibold text-gray-500 flex-shrink-0">Presets:</span>
                  {PRESET_IMAGES.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => set("coverImage", img)}
                      className={`w-14 h-9 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                        form.coverImage === img ? "border-[#e5243b] scale-105" : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img src={img} alt="Preset" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Summary / Excerpt
                </label>
                <textarea
                  rows={2}
                  className={inputCls}
                  value={form.excerpt}
                  onChange={(e) => set("excerpt", e.target.value)}
                  placeholder="A concise 1-2 sentence overview of the article shown in listings..."
                />
              </div>

              {/* Full Content */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  Article Body (Supports Markdown: ### Headings, - Bullet points, --- Dividers) *
                </label>
                <textarea
                  rows={10}
                  className={`${inputCls} font-mono text-xs sm:text-sm`}
                  value={form.content}
                  onChange={(e) => set("content", e.target.value)}
                  placeholder="Write your article here..."
                  required
                />
              </div>

              {/* Published Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={form.published}
                  onChange={(e) => set("published", e.target.checked)}
                  className="w-4 h-4 text-[#e5243b] rounded focus:ring-[#e5243b]"
                />
                <label htmlFor="published" className="text-sm font-bold text-gray-800 cursor-pointer">
                  Published (Visible to all website visitors)
                </label>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="blog-form"
            className="px-6 py-2 text-sm font-extrabold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-md transition-all"
          >
            {isNew ? "Create Article" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;
